/*
| Protocol analytics, as data.
|
| Sources (all metered public reads):
|   /analytics/protocol/totals   this window, and the previous one for deltas
|   /analytics/protocol/series   per bucket, oldest first
|   /analytics/markets/volume    per market, largest first
|   /analytics/treasury/net-fees all-time, not windowed
|   /analytics/tokens            TVL now, until the totals carry it
|
| Volume, trades and the spot/perp split count the builders the explorer
| displays; fees, takers, new traders and per-market volume count every trade.
| That is why market shares are taken against the market rows themselves.
|
| TVL, deposits, withdrawals, net flow and active accounts (traded, deposited
| or withdrew) come from the same totals and series; USDC only, withdrawals
| counted at initiation. A response without them leaves those panels unmeasured.
*/

import { marketHref, pairsById } from '../lib/catalog.js';
import { toMs, toNumber } from '../lib/values.js';

/** Percent change against the previous window; null when there is nothing to compare. */
export function changePercent(current, previous) {
  const now = toNumber(current);
  const before = toNumber(previous);
  if (now === null || before === null || before === 0) return null;
  return ((now - before) / Math.abs(before)) * 100;
}

/** A KPI in the shape the readiness helpers render: value plus change. */
export function metric(value, previousValue) {
  return { value: value ?? null, readiness: 'live', change_percent: changePercent(value, previousValue) };
}

const has = (object, key) => object !== null && object !== undefined && object[key] !== undefined;

export function protocolKpis({ totals, previous, netFees, custody }) {
  const t = totals ?? {};
  const p = previous ?? {};

  // The windowed TVL wins once it has a value; before the custody fold has one,
  // custody now stands in.
  const tvl = has(totals, 'tvlUsd') && totals.tvlUsd !== null
    ? { value: t.tvlUsd ?? null, readiness: 'live', change_percent: changePercent(t.tvlUsd, t.openingTvlUsd), note: null }
    : { value: custody?.tvlUsd ?? null, readiness: 'live', change_percent: null, note: 'now' };

  const spot = toNumber(t.spotVolumeUsd);
  const perp = toNumber(t.perpVolumeUsd);

  return {
    volume: metric(t.volumeUsd, p.volumeUsd),
    trades: metric(t.tradesCount, p.tradesCount),
    newTraders: metric(t.newTraders, p.newTraders),
    fees: metric(t.protocolFeesUsd, p.protocolFeesUsd),
    netFees: { value: netFees?.netFeesUsd ?? null, readiness: 'live', change_percent: null },
    tvl,
    split: spot === null && perp === null ? null : { spot: spot ?? 0, perp: perp ?? 0 },
    // USDC flows and the account union (traded, deposited or withdrew).
    deposits: metric(t.depositsUsd, p.depositsUsd),
    withdrawals: metric(t.withdrawalsUsd, p.withdrawalsUsd),
    netFlow: metric(t.netFlowUsd, p.netFlowUsd),
    activeAccounts: metric(t.activeAccounts, p.activeAccounts),
  };
}

export function volumePoints(series) {
  return (series?.points ?? []).map((point) => ({
    at: toMs(point.bucketStart),
    spot: toNumber(point.spotVolumeUsd),
    perp: toNumber(point.perpVolumeUsd),
    total: toNumber(point.volumeUsd),
  }));
}

/** TVL per bucket, when the series carries it. */
export function tvlPoints(series) {
  const points = series?.points ?? [];
  if (!points.some((point) => has(point, 'tvlUsd'))) return { available: false, points: [] };
  return { available: true, points: points.map((point) => ({ at: toMs(point.bucketStart), tvl: toNumber(point.tvlUsd) })) };
}

/** Deposits, withdrawals and net flow per bucket, when the series carries them. */
export function flowPoints(series) {
  const points = series?.points ?? [];
  if (!points.some((point) => has(point, 'depositsUsd'))) return { available: false, points: [] };
  return {
    available: true,
    points: points.map((point) => ({
      at: toMs(point.bucketStart),
      deposits: toNumber(point.depositsUsd),
      withdrawals: toNumber(point.withdrawalsUsd),
      net: toNumber(point.netFlowUsd),
    })),
  };
}

export function marketVolumeRows(markets, pairs) {
  const byId = pairsById(pairs);
  const total = (markets ?? []).reduce((sum, row) => sum + (toNumber(row.volumeUsd) ?? 0), 0);

  return (markets ?? []).map((row) => {
    const pair = byId.get(row.tradingPairId) ?? null;
    const volume = toNumber(row.volumeUsd) ?? 0;
    return {
      pairId: row.tradingPairId,
      symbol: pair?.symbol ?? null,
      href: pair ? marketHref(pairs, pair) : null,
      volumeUsd: row.volumeUsd ?? null,
      sharePercent: total > 0 ? (volume / total) * 100 : 0,
      tradesCount: row.tradesCount ?? null,
      feesUsd: row.protocolFeesUsd ?? null,
    };
  });
}
